import { AWS_SDK_CUSTOM_USER_AGENT as AWS_SDK_CUSTOM_USER_AGENT$1, AwsResourceTaggingApiLocator as AwsResourceTaggingApiLocator$1 } from '@aws/aws-core-plugin-for-backstage-common';
import { ResourceExplorer2Client, SearchCommand } from '@aws-sdk/client-resource-explorer-2';
import { DefaultAwsCredentialsManager } from '@backstage/integration-aws-node';
import { ConfigServiceClient, paginateSelectAggregateResourceConfig, paginateSelectResourceConfig } from '@aws-sdk/client-config-service';
import { ResourceGroupsTaggingAPIClient, GetResourcesCommand } from '@aws-sdk/client-resource-groups-tagging-api';

function parseResourceLocatorTags(tagString) {
  return tagString.split(",").map((e) => {
    const parts = e.split("=");
    return {
      key: parts[0],
      value: parts[1]
    };
  });
}
function convertResourceTypeString(resourceType) {
  const parts = resourceType.split("::");
  if (parts.length !== 3) {
    throw new Error(`Invalid resource type ${resourceType}`);
  }
  return `${parts[1].toLowerCase()}:${parts[2].toLowerCase()}`;
}

class AwsResourceExplorerLocator {
  constructor(logger, client, viewArn) {
    this.logger = logger;
    this.client = client;
    this.viewArn = viewArn;
  }
  static async fromConfig(config, options) {
    let region;
    let accountId;
    let viewArn;
    const conf = config.getOptionalConfig("aws.locator.resourceExplorer");
    if (conf) {
      viewArn = conf.getOptionalString("viewArn");
      accountId = conf.getOptionalString("accountId");
      region = conf.getOptionalString("region");
    }
    const credsManager = DefaultAwsCredentialsManager.fromConfig(config);
    let credentialProvider;
    if (accountId) {
      credentialProvider = (await credsManager.getCredentialProvider({ accountId })).sdkCredentialProvider;
    } else {
      credentialProvider = (await credsManager.getCredentialProvider()).sdkCredentialProvider;
    }
    const client = new ResourceExplorer2Client({
      region,
      customUserAgent: AWS_SDK_CUSTOM_USER_AGENT$1,
      credentialDefaultProvider: () => credentialProvider
    });
    return new AwsResourceExplorerLocator(options.logger, client, viewArn);
  }
  async getResourceArns({
    resourceType,
    tagString
  }) {
    const tagFilters = parseResourceLocatorTags(tagString);
    const convertedResourceType = convertResourceTypeString(resourceType);
    this.logger.debug(`Retrieving resource ARNs for ${convertedResourceType}`);
    const query = `${tagFilters.map((e) => {
      return `tag:${e.key}=${e.value}`;
    }).join(" ")} resourcetype:${convertedResourceType}`.trim();
    this.logger.debug(`Resource explorer query: ${query}`);
    const response = await this.client.send(
      new SearchCommand({
        QueryString: query,
        ViewArn: this.viewArn
      })
    );
    return response.Resources.map((e) => e.Arn);
  }
}

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const _AwsConfigResourceLocator = class _AwsConfigResourceLocator {
  constructor(logger, client, aggregatorName) {
    this.logger = logger;
    this.client = client;
    this.aggregatorName = aggregatorName;
  }
  static async fromConfig(config, options) {
    let region;
    let accountId;
    let aggregatorName;
    const conf = config.getOptionalConfig("aws.locator.awsConfig");
    if (conf) {
      aggregatorName = conf.getOptionalString("aggregatorName");
      accountId = conf.getOptionalString("accountId");
      region = conf.getOptionalString("region");
    }
    const credsManager = DefaultAwsCredentialsManager.fromConfig(config);
    let credentialProvider;
    if (accountId) {
      credentialProvider = (await credsManager.getCredentialProvider({ accountId })).sdkCredentialProvider;
    } else {
      credentialProvider = (await credsManager.getCredentialProvider()).sdkCredentialProvider;
    }
    const client = new ConfigServiceClient({
      region,
      customUserAgent: AWS_SDK_CUSTOM_USER_AGENT$1,
      credentialDefaultProvider: () => credentialProvider
    });
    return new _AwsConfigResourceLocator(options.logger, client, aggregatorName);
  }
  async getResourceArns({
    resourceType,
    tagString
  }) {
    const tagFilters = parseResourceLocatorTags(tagString);
    const convertedResourceType = convertResourceTypeString(resourceType);
    this.logger.debug(`Retrieving resource ARNs for ${convertedResourceType}`);
    const whereClause = `${tagFilters.map((e) => {
      return `tags.tag='${e.key}=${e.value}'`;
    }).join(" AND ")} resourceType='${resourceType}'`.trim();
    const query = `${_AwsConfigResourceLocator.AWS_CONFIG_QUERY_TEMPLATE} WHERE ${whereClause}`;
    this.logger.debug(`AWS Config query: ${query}`);
    let paginator;
    if (this.aggregatorName) {
      paginator = paginateSelectAggregateResourceConfig(
        {
          client: this.client,
          pageSize: 25
        },
        {
          Expression: query,
          ConfigurationAggregatorName: this.aggregatorName
        }
      );
    } else {
      paginator = paginateSelectResourceConfig(
        {
          client: this.client,
          pageSize: 25
        },
        {
          Expression: query
        }
      );
    }
    let arns = [];
    for await (const page of paginator) {
      arns = arns.concat(
        page.Results.map((e) => {
          return JSON.parse(e).arn;
        })
      );
    }
    return arns;
  }
};
__publicField(_AwsConfigResourceLocator, "AWS_CONFIG_QUERY_TEMPLATE", "SELECT arn");
let AwsConfigResourceLocator = _AwsConfigResourceLocator;

class AwsResourceLocatorFactory {
  static async fromConfig(config, logger) {
    const conf = config.getOptionalConfig("aws.locator");
    if (conf) {
      const locatorType = conf.getOptionalString("type");
      switch (locatorType) {
        case "resourceExplorer":
          return await AwsResourceExplorerLocator.fromConfig(config, {
            logger
          });
        case "awsConfig":
          return await AwsConfigResourceLocator.fromConfig(config, {
            logger
          });
        default:
          logger.warn("Defaulting to resource tagging API locator");
      }
    }
    return await AwsResourceTaggingApiLocator$1.fromConfig(config, {
      logger
    });
  }
}

const AWS_SDK_CUSTOM_USER_AGENT = "backstage-plugins-for-aws";

class AwsResourceTaggingApiLocatorInstance {
  constructor(logger, client) {
    this.logger = logger;
    this.client = client;
  }
  static async create(options) {
    let credentialProvider;
    const { accountId, region, credsManager } = options;
    if (accountId) {
      credentialProvider = (await credsManager.getCredentialProvider({
        accountId
      })).sdkCredentialProvider;
    } else {
      credentialProvider = (await credsManager.getCredentialProvider()).sdkCredentialProvider;
    }
    const client = new ResourceGroupsTaggingAPIClient({
      region,
      customUserAgent: AWS_SDK_CUSTOM_USER_AGENT,
      credentialDefaultProvider: () => credentialProvider
    });
    return new AwsResourceTaggingApiLocatorInstance(options.logger, client);
  }
  async getResourceArns({
    resourceType,
    tagString
  }) {
    const TagFilters = parseResourceLocatorTags(tagString).map((e) => {
      return {
        Key: e.key,
        Values: [e.value]
      };
    });
    const convertedResourceType = convertResourceTypeString(resourceType);
    this.logger.debug(`Retrieving resource ARNs for ${convertedResourceType}`);
    const response = await this.client.send(
      new GetResourcesCommand({
        ResourceTypeFilters: [convertedResourceType],
        TagFilters
      })
    );
    return response.ResourceTagMappingList.map((e) => e.ResourceARN);
  }
}

class AwsResourceTaggingApiLocator {
  constructor(instances) {
    this.instances = instances;
  }
  static async fromConfig(config, options) {
    let regions = [void 0];
    let accounts = [void 0];
    const conf = config.getOptionalConfig("aws.locator.resourceTaggingApi");
    if (conf) {
      accounts = conf.getOptionalStringArray("accounts") || [void 0];
      regions = conf.getOptionalStringArray("regions") || [void 0];
    }
    const credsManager = DefaultAwsCredentialsManager.fromConfig(config);
    const instances = await Promise.all(
      accounts.flatMap((accountId) => {
        return regions.map((region) => {
          options.logger.debug(`Creating client for ${accountId} - ${region}`);
          return AwsResourceTaggingApiLocatorInstance.create({
            credsManager,
            accountId,
            region,
            logger: options.logger
          });
        });
      })
    );
    return new AwsResourceTaggingApiLocator(instances);
  }
  async getResourceArns({
    resourceType,
    tagString
  }) {
    return (await Promise.all(
      this.instances.map((e) => e.getResourceArns({ resourceType, tagString }))
    )).flat(1);
  }
}

function getOneOfEntityAnnotations(entity, targetAnnotations) {
  if (entity.metadata.annotations) {
    const annotations = Object.keys(entity.metadata.annotations);
    const intersection = annotations.filter(
      (value) => targetAnnotations.includes(value)
    );
    if (intersection.length === 1) {
      const match = intersection[0];
      return {
        name: match,
        value: entity.metadata.annotations[match]
      };
    }
    return void 0;
  }
  return void 0;
}

function generateShortcutLink(ssoSubdomain, accountId, projectUrl) {
  return `https://${ssoSubdomain}.awsapps.com/start/#/console?account_id=${accountId}&destination=${encodeURIComponent(
    projectUrl
  )}`;
}

export { AWS_SDK_CUSTOM_USER_AGENT, AwsConfigResourceLocator, AwsResourceExplorerLocator, AwsResourceLocatorFactory, AwsResourceTaggingApiLocator, generateShortcutLink, getOneOfEntityAnnotations };
//# sourceMappingURL=index.esm.js.map
